import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';

// Wake Lock API types
interface WakeLockSentinel {
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
  removeEventListener(type: 'release', listener: () => void): void;
}

interface NavigatorWithWakeLock {
  wakeLock?: {
    request(type: 'screen'): Promise<WakeLockSentinel>;
  };
}

interface WakeLockContextValue {
  isToggled: boolean;
  wakeLockSupported: boolean;
  toggle: () => void;
}

const WakeLockContext = createContext<WakeLockContextValue | undefined>(undefined);

export const useWakeLock = () => {
  const context = useContext(WakeLockContext);
  if (!context) {
    throw new Error('useWakeLock must be used within WakeLockProvider');
  }
  return context;
};

export const WakeLockProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isToggled, setIsToggled] = useState(false);
  const [wakeLockSupported, setWakeLockSupported] = useState(false);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  // Check Wake Lock API support on mount
  useEffect(() => {
    const navigatorWithWakeLock = navigator as NavigatorWithWakeLock;
    setWakeLockSupported(!!navigatorWithWakeLock.wakeLock);
  }, []);

  // Request wake lock
  const requestWakeLock = useCallback(async () => {
    try {
      const navigatorWithWakeLock = navigator as NavigatorWithWakeLock;
      if (navigatorWithWakeLock.wakeLock) {
        wakeLockRef.current = await navigatorWithWakeLock.wakeLock.request('screen');

        // Handle wake lock release
        wakeLockRef.current.addEventListener('release', () => {
          wakeLockRef.current = null;
          setIsToggled(false);
        });
      } else {
        console.warn('Wake Lock API not supported in this browser');
      }
    } catch (err) {
      console.error('Failed to request wake lock:', err);
      setIsToggled(false);
    }
  }, []);

  // Release wake lock
  const releaseWakeLock = useCallback(async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch (err) {
      console.error('Failed to release wake lock:', err);
    }
  }, []);

  // Handle wake lock based on toggle state
  useEffect(() => {
    if (isToggled) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    // Cleanup on unmount
    return () => {
      releaseWakeLock();
    };
  }, [isToggled, requestWakeLock, releaseWakeLock]);

  // Toggle function
  const toggle = useCallback(() => {
    if (wakeLockSupported) {
      setIsToggled((prev) => !prev);
    } else {
      console.warn('Wake Lock API not supported - toggle functionality disabled');
    }
  }, [wakeLockSupported]);

  const value: WakeLockContextValue = {
    isToggled,
    wakeLockSupported,
    toggle,
  };

  return <WakeLockContext.Provider value={value}>{children}</WakeLockContext.Provider>;
};
